import { useState, useEffect, useContext, useMemo } from 'react';
import { toast } from 'react-toastify';
import { Link, useLocation } from 'react-router-dom';

import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';

import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import BlockIcon from '@mui/icons-material/Block';
import SmsIcon from '@mui/icons-material/Sms';
import EmailIcon from '@mui/icons-material/Email';
import PhoneCallbackIcon from '@mui/icons-material/PhoneCallback';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SearchIcon from '@mui/icons-material/Search';

import { Spinner, BrowserTitle } from '../components';
import ScheduledAppointmentModal from '../components/ScheduledAppointment/ScheduledAppointmentModal';

import { AuthContext } from '../context/AuthProvider';

import scheduledAppointmentsServices from '../services/scheduledAppointmentsServices';

const ADMIN_ROLES = ['superadmin', 'admin', 'dispatcher'];

const STATUS_CHIP = {
  scheduled: { label: 'Scheduled', color: 'primary' },
  notified: { label: 'Notified', color: 'warning' },
  completed: { label: 'Call Done', color: 'success' },
  cancelled: { label: 'Cancelled', color: 'default' },
};

const FILTERS = [
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'notified', label: 'Notified' },
  { value: 'completed', label: 'Call Done' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'all', label: 'All' },
];

const formatDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const NotifyIcons = ({ item }) => {
  const parts = [];
  if (item.notifyClientSms) parts.push({ key: 'cs', icon: <SmsIcon fontSize="inherit" />, label: 'SMS to client' });
  if (item.notifyClientEmail) parts.push({ key: 'ce', icon: <EmailIcon fontSize="inherit" />, label: 'Email to client' });
  if (item.notifySalesmanSms) parts.push({ key: 'ss', icon: <SmsIcon fontSize="inherit" />, label: 'SMS to salesman' });
  if (item.notifySalesmanEmail) parts.push({ key: 'se', icon: <EmailIcon fontSize="inherit" />, label: 'Email to salesman' });

  if (!parts.length) return <Typography variant="caption" color="text.secondary">None</Typography>;

  const client = parts.filter((p) => p.key.startsWith('c'));
  const salesman = parts.filter((p) => p.key.startsWith('s'));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
      {client.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 16 }}>
          <Typography variant="caption" color="text.secondary" sx={{ width: 62 }}>Client:</Typography>
          {client.map((p) => (
            <Tooltip key={p.key} title={p.label}>{p.icon}</Tooltip>
          ))}
        </Box>
      )}
      {salesman.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 16 }}>
          <Typography variant="caption" color="text.secondary" sx={{ width: 62 }}>Salesman:</Typography>
          {salesman.map((p) => (
            <Tooltip key={p.key} title={p.label}>{p.icon}</Tooltip>
          ))}
        </Box>
      )}
    </Box>
  );
};

export const ScheduledAppointments = () => {
  const { userData } = useContext(AuthContext);
  const isAdmin = ADMIN_ROLES.includes(userData?.role);
  const location = useLocation();
  // Filters restored when coming back from a client card (see the client link state below)
  const restored = location.state || {};

  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState(restored.filter || 'upcoming');
  const [search, setSearch] = useState(restored.search || '');
  const [salesmanFilter, setSalesmanFilter] = useState(restored.salesmanFilter || 'all');
  const [modalOpen, setModalOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [completeItem, setCompleteItem] = useState(null);
  const [completionNote, setCompletionNote] = useState('');
  const [completing, setCompleting] = useState(false);

  const loadData = async () => {
    try {
      const response = await scheduledAppointmentsServices.getAll({ take: 1000 });
      setItems(response.data || []);
    } catch (error) {
      console.error('Failed to load scheduled appointments:', error);
      toast.error('Failed to load scheduled appointments');
      setItems([]);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Unique salesmen present in the loaded list (for the admin filter)
  const salesmen = useMemo(() => {
    if (!items) return [];
    const map = new Map();
    items.forEach((i) => {
      if (i.salesman?.id) map.set(i.salesman.id, i.salesman.fullName || `#${i.salesman.id}`);
    });
    return [...map.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [items]);

  const filtered = useMemo(() => {
    if (!items) return [];

    let list;
    switch (filter) {
      case 'upcoming':
        list = items.filter((i) => i.status === 'scheduled');
        break;
      case 'notified':
        list = items.filter((i) => i.status === 'notified');
        break;
      case 'completed':
        list = items.filter((i) => i.status === 'completed');
        break;
      case 'cancelled':
        list = items.filter((i) => i.status === 'cancelled');
        break;
      default:
        list = items;
    }

    if (salesmanFilter !== 'all') {
      list = list.filter((i) => i.salesman?.id === salesmanFilter);
    }

    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((i) => {
        const name = (i.client?.fullName || '').toLowerCase();
        const phone = (i.client?.phoneNumber || '').toLowerCase();
        const addresses = i.client?.addresses || [];
        return (
          name.includes(q) ||
          phone.includes(q) ||
          addresses.some((a) => (a || '').toLowerCase().includes(q))
        );
      });
    }

    return list;
  }, [items, filter, search, salesmanFilter]);

  const setStatus = async (item, status) => {
    try {
      await scheduledAppointmentsServices.update(item.id, { status });
      toast.success('Appointment cancelled');
      loadData();
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error(error?.response?.data?.message || 'Failed to update status');
    }
  };

  const handleCompleteConfirm = async () => {
    if (!completeItem) return;
    setCompleting(true);
    try {
      await scheduledAppointmentsServices.update(completeItem.id, {
        status: 'completed',
        completionNote: completionNote.trim() || null,
      });
      toast.success('Marked as Call Done');
      setCompleteItem(null);
      setCompletionNote('');
      loadData();
    } catch (error) {
      console.error('Failed to complete:', error);
      toast.error(error?.response?.data?.message || 'Failed to update status');
    } finally {
      setCompleting(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm('Delete this scheduled appointment?')) return;
    try {
      await scheduledAppointmentsServices.remove(item.id);
      toast.success('Scheduled appointment deleted');
      loadData();
    } catch (error) {
      console.error('Failed to delete:', error);
      toast.error(error?.response?.data?.message || 'Failed to delete');
    }
  };

  if (!items) return <Spinner />;

  return (
    <Box>
      <BrowserTitle title="Scheduled Appointments | Airmaxx Pro" />

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PhoneCallbackIcon color="primary" />
          <Typography variant="h5">Scheduled Appointments</Typography>
        </Box>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setEditItem(null);
            setModalOpen(true);
          }}
        >
          Add to schedule
        </Button>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={filter}
          onChange={(e, value) => value && setFilter(value)}
        >
          {FILTERS.map((f) => (
            <ToggleButton key={f.value} value={f.value}>
              {f.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <TextField
          size="small"
          placeholder="Search by client name or address"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 280 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        {isAdmin && (
          <TextField
            select
            size="small"
            label="Salesman"
            value={salesmanFilter}
            onChange={(e) => setSalesmanFilter(e.target.value)}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="all">All salesmen</MenuItem>
            {salesmen.map((s) => (
              <MenuItem key={s.id} value={s.id}>
                {s.name}
              </MenuItem>
            ))}
          </TextField>
        )}
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Call Date & Time</TableCell>
              <TableCell>Client</TableCell>
              <TableCell>Salesman</TableCell>
              <TableCell>Notifications</TableCell>
              <TableCell>Note</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No scheduled appointments{filter !== 'all' ? ` in "${FILTERS.find((f) => f.value === filter)?.label}"` : ''}
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {filtered.map((item) => {
              const overdue = item.status === 'scheduled' && new Date(item.scheduledFor).getTime() < Date.now();
              const chip = STATUS_CHIP[item.status] || { label: item.status, color: 'default' };

              return (
                <TableRow key={item.id} hover sx={overdue ? { backgroundColor: '#fff8e1' } : undefined}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formatDateTime(item.scheduledFor)}
                    </Typography>
                    {item.notifyError && (
                      <Tooltip title={item.notifyError}>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: 'error.main' }}>
                          <ErrorOutlineIcon sx={{ fontSize: 14 }} />
                          <Typography variant="caption">delivery issues</Typography>
                        </Box>
                      </Tooltip>
                    )}
                  </TableCell>

                  <TableCell>
                    {item.client ? (
                      <>
                        <Typography
                          variant="body2"
                          component={Link}
                          to={`/clients/${item.client.id}`}
                          state={{ from: 'scheduled-appointments', filter, search, salesmanFilter }}
                          sx={{ textDecoration: 'none', color: 'primary.main', fontWeight: 600 }}
                        >
                          {item.client.fullName || '—'}
                        </Typography>
                        {item.client.phoneNumber && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            {item.client.phoneNumber}
                          </Typography>
                        )}
                        {(item.client.addresses || []).slice(0, 1).map((addr) => (
                          <Typography key={addr} variant="caption" display="block" color="text.secondary">
                            {addr}
                          </Typography>
                        ))}
                      </>
                    ) : (
                      '—'
                    )}
                  </TableCell>

                  <TableCell>{item.salesman?.fullName || '—'}</TableCell>

                  <TableCell>
                    <NotifyIcons item={item} />
                  </TableCell>

                  <TableCell sx={{ maxWidth: 260 }}>
                    <Tooltip title={item.note || ''}>
                      <Typography variant="body2" noWrap>
                        {item.note || '—'}
                      </Typography>
                    </Tooltip>
                    {item.completionNote && (
                      <Tooltip title={item.completionNote}>
                        <Typography variant="caption" noWrap display="block" sx={{ color: 'success.main' }}>
                          Result: {item.completionNote}
                        </Typography>
                      </Tooltip>
                    )}
                  </TableCell>

                  <TableCell>
                    <Chip size="small" label={chip.label} color={chip.color} variant={item.status === 'cancelled' ? 'outlined' : 'filled'} />
                  </TableCell>

                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {['scheduled', 'notified'].includes(item.status) && (
                      <Tooltip title="Mark as Call Done">
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() => {
                            setCompleteItem(item);
                            setCompletionNote(item.completionNote || '');
                          }}
                        >
                          <CheckCircleOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}

                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setEditItem(item);
                          setModalOpen(true);
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    {item.status === 'scheduled' && (
                      <Tooltip title="Cancel (no notifications will be sent)">
                        <IconButton size="small" color="warning" onClick={() => setStatus(item, 'cancelled')}>
                          <BlockIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}

                    {isAdmin && (
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => handleDelete(item)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <ScheduledAppointmentModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={loadData}
        editItem={editItem}
      />

      {/* Call Done confirmation with a short call summary */}
      <Dialog open={!!completeItem} onClose={() => setCompleteItem(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Mark as Call Done</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {completeItem?.client?.fullName
              ? `Follow-up call with ${completeItem.client.fullName}`
              : 'Follow-up call'}{' '}
            — add a short summary of the conversation (optional).
          </Typography>
          <TextField
            autoFocus
            label="Call summary"
            placeholder="e.g. Client agreed to proceed, sending updated proposal next week"
            value={completionNote}
            onChange={(e) => setCompletionNote(e.target.value)}
            multiline
            minRows={3}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompleteItem(null)} disabled={completing}>
            Cancel
          </Button>
          <Button variant="contained" color="success" onClick={handleCompleteConfirm} disabled={completing}>
            {completing ? 'Saving...' : 'Call Done'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ScheduledAppointments;
